import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Widget } from './Widget';
import { VCScanFaceGlyph } from './components/glyphs';
import { lockBodyScroll, unlockBodyScroll } from './lib/scrollLock';
import { focusInitialElement, handleFocusTrapKeydown } from './lib/focusTrap';
import { applyClinicBranding, loadClinicConfig } from './lib/clinicConfig';
import { t, setWidgetLocale } from './i18n';

const CloseGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

export const OPEN_INTAKE_EVENT = 'beauty-advisor:open-intake';

declare global {
  interface Window {
    BeautyAdvisorWidget?: { open: () => void };
  }
}

interface EmbedShellProps {
  clinicSlug: string;
  apiBase: string;
  shadowHost: HTMLElement;
}

export function EmbedShell({ clinicSlug, apiBase, shadowHost }: EmbedShellProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Bumped when clinic-config lands so the FAB re-renders with the clinic's
  // locale (label/aria). Brand colors are applied directly to the shadow host.
  const [, setConfigRevision] = useState(0);
  const fabRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Prefetch clinic-config at mount so the launcher FAB is clinic-tinted and
  // localized before the widget is first opened. Failures are silent here —
  // the FAB keeps the default theme and Widget surfaces its existing error
  // state on open (the loader cache is cleared on rejection, so opening
  // retries the fetch).
  useEffect(() => {
    let cancelled = false;
    loadClinicConfig(apiBase, clinicSlug)
      .then((config) => {
        if (cancelled) return;
        applyClinicBranding(shadowHost, config);
        setWidgetLocale(config.locale);
        shadowHost.setAttribute('lang', config.locale);
        setConfigRevision((revision) => revision + 1);
      })
      .catch(() => {
        /* handled on open by Widget */
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, clinicSlug, shadowHost]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    shadowHost.classList.toggle('is-open', isOpen);
  }, [isOpen, shadowHost]);

  useEffect(() => {
    if (!isOpen) {
      unlockBodyScroll();
      fabRef.current?.focus();
      return;
    }

    lockBodyScroll();
    const dialog = dialogRef.current;
    if (dialog) {
      requestAnimationFrame(() => focusInitialElement(dialog));
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (dialog) {
        handleFocusTrapKeydown(event, dialog);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      unlockBodyScroll();
    };
  }, [isOpen, close]);

  useEffect(() => () => unlockBodyScroll(), []);

  useEffect(() => {
    const handler = () => open();
    window.addEventListener(OPEN_INTAKE_EVENT, handler);
    window.BeautyAdvisorWidget = { open };
    return () => {
      window.removeEventListener(OPEN_INTAKE_EVENT, handler);
      delete window.BeautyAdvisorWidget;
    };
  }, [open]);

  return (
    <div className="embed-root">
      <button
        ref={fabRef}
        type="button"
        className={`embed-fab${isOpen ? ' is-hidden' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={t('launcher.ariaOpen')}
        tabIndex={isOpen ? -1 : 0}
        onClick={open}
      >
        <span className="embed-fab-sheen" aria-hidden="true" />
        <span className="embed-fab-label">{t('launcher.label')}</span>
        <span className="embed-fab-cap">
          <VCScanFaceGlyph />
        </span>
      </button>

      {isOpen && (
        <>
          <div
            className="embed-backdrop"
            data-close="true"
            aria-hidden="true"
            onClick={close}
          />
          <div className="embed-stage">
            <div
              ref={dialogRef}
              className="embed-modal"
              role="dialog"
              aria-modal="true"
              aria-label={t('shell.dialogAria')}
            >
              <button
                type="button"
                className="embed-modal-close"
                aria-label={t('shell.closeAria')}
                onClick={close}
              >
                <CloseGlyph />
              </button>
              <Widget clinicSlug={clinicSlug} apiBase={apiBase} shadowHost={shadowHost} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
